import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { qualityReqApi, F11H } from '../../../services/qualityReqApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useQualityReqForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F11H[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [desc, setDesc] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await qualityReqApi.list(1, 1000); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') { setCode(current.f11h_01); setDesc(current.f11h_11); }
  }, [current, mode]);

  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };
  const startAdd = () => { setMode('add'); setCode(''); setDesc(''); };
  const startEdit = () => { if (!current) return; setMode('edit'); setCode(current.f11h_01); setDesc(current.f11h_11); };
  const cancel = () => { setMode('browse'); if (current) { setCode(current.f11h_01); setDesc(current.f11h_11); } };

  const save = async () => {
    try {
      if (mode === 'add') { await qualityReqApi.create({ f11h_01: code, f11h_11: desc }); message.success('新增成功'); }
      else { await qualityReqApi.update(current!.f11h_01, { f11h_11: desc }); message.success('修改成功'); }
      const data = await loadRecords();
      const t = mode === 'add' ? code : current!.f11h_01;
      const idx = data.findIndex(r => r.f11h_01 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await qualityReqApi.delete(current.f11h_01); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f11h_01 >= t);
    if (idx >= 0) setCurrentIdx(idx);
    else message.warning('查無此代號');
  };

  return { mode, isEditing, loading, records, current, currentIdx, code, setCode, desc, setDesc,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
